import type { RxCollection } from 'rxdb';

import { countTokens, stripHtml, getCurrDatetime } from '@flintmint/core-shared/utils';
import type { Block, Note, Tag, UserSource, Instruction, RxDatabaseCollections, RxJSON } from '@flintmint/core-shared/rxdb';

import {
  syncNoteBlockReferences,
  removeReferenceFromNotes,
  updateNotesReferencingBlock,
  cleanupTemplateReferencesOnDelete,
  cleanupDeletedInstructionReferences,
} from './note-handler';
import { detachTagFromDocs } from './tag-handler';
import { MAX_DOCUMENT_NAME_LENGTH } from '@/extensions/content-size-limit';

type MiddlewareHook = (rxJSON: any, rxDocument?: any) => Promise<any>;
type MiddlewareBuilderResponse = MiddlewareHook | undefined;

export const getPreInsert = ({
  rxCollection,
  rxCollections,
}: {
  rxCollection: RxCollection;
  rxCollections: RxDatabaseCollections;
}): MiddlewareBuilderResponse => {
  if (rxCollection.name === 'notes') {
    return preInsertNote(rxCollections.instructions, rxCollections.blocks);
  }

  if (rxCollection.name === 'blocks') {
    return preInsertBlock(rxCollections.instructions);
  }

  if (rxCollection.name === 'instructions') {
    return preInsertInstruction();
  }

  if (rxCollection.name === 'userSources') {
    return preInsertUserSource();
  }

  return undefined;
};

const preInsertNote = (instructionsCollection: RxCollection<Instruction>, blocksCollection: RxCollection<Block>) => {
  return async (note: Note) => {
    note.name = capDocumentName(note.name);
    note.numTokens = countTokens([note.content, note.name]);
    note.parsedContent = stripHtml(note.content);
    note.instructions = await duplicateDocumentInstructions(instructionsCollection, note);

    // Content pasted into a new note can already carry block instances, so the block references
    // have to be derived here too: the save-time sync would otherwise only reach them on a later
    // edit, leaving the pasted blocks' instructions invisible until then.
    await runBestEffort('note block-reference sync', () => syncNoteBlockReferences(blocksCollection, note));
  };
};

const preInsertBlock = (instructionsCollection: RxCollection<Instruction>) => {
  return async (block: Block) => {
    block.name = capDocumentName(block.name);
    block.instructions = await duplicateDocumentInstructions(instructionsCollection, block);
  };
};

const preInsertInstruction = () => {
  return async (instruction: Instruction) => {
    instruction.numTokens = countTokens([instruction.content, instruction.name]);
    instruction.parsedContent = stripHtml(instruction.content);
  };
};

const preInsertUserSource = () => {
  return async (userSource: UserSource) => {
    userSource.name = capDocumentName(userSource.name);
    userSource.numTokens = countTokens([userSource.content, userSource.name]);
    userSource.parsedContent = stripHtml(userSource.content);
  };
};

export const getPreSave = ({
  rxCollection,
  rxCollections,
}: {
  rxCollection: RxCollection;
  rxCollections: RxDatabaseCollections;
}): MiddlewareBuilderResponse => {
  if (rxCollection.name === 'notes') {
    return preSaveNote(rxCollections.blocks);
  }

  if (rxCollection.name === 'blocks') {
    return preSaveBlock();
  }

  if (rxCollection.name === 'instructions') {
    return preSaveInstruction();
  }

  if (rxCollection.name === 'userSources') {
    return preSaveUserSource();
  }

  return undefined;
};

const preSaveNote = (blocksCollection: RxCollection<Block>) => {
  return async (updatedNote: Note, oldNote: Note) => {
    updatedNote.name = capDocumentName(updatedNote.name);
    if (updatedNote.content !== oldNote.content || updatedNote.name !== oldNote.name) {
      updatedNote.numTokens = countTokens([updatedNote.content, updatedNote.name]);
    }

    if (updatedNote.content !== oldNote.content) {
      updatedNote.parsedContent = stripHtml(updatedNote.content);
    }

    // Best-effort denormalization side-effect: a rejection must not abort the
    // user's note save, but is logged so it surfaces in Sentry instead of vanishing.
    await runBestEffort('note block-reference sync', () => syncNoteBlockReferences(blocksCollection, updatedNote));
  };
};

const preSaveBlock = () => {
  return async (updatedBlock: Block) => {
    updatedBlock.name = capDocumentName(updatedBlock.name);
  };
};

const preSaveInstruction = () => {
  return async (updatedInstruction: Instruction, oldInstruction: Instruction) => {
    if (updatedInstruction.content !== oldInstruction.content || updatedInstruction.name !== oldInstruction.name) {
      updatedInstruction.numTokens = countTokens([updatedInstruction.content, updatedInstruction.name]);
    }

    if (updatedInstruction.content !== oldInstruction.content) {
      updatedInstruction.parsedContent = stripHtml(updatedInstruction.content);
    }
  };
};

const preSaveUserSource = () => {
  return async (updatedUserSource: UserSource, oldUserSource: UserSource) => {
    updatedUserSource.name = capDocumentName(updatedUserSource.name);
    if (updatedUserSource.content !== oldUserSource.content || updatedUserSource.name !== oldUserSource.name) {
      updatedUserSource.numTokens = countTokens([updatedUserSource.content, updatedUserSource.name]);
    }

    if (updatedUserSource.content !== oldUserSource.content) {
      updatedUserSource.parsedContent = stripHtml(updatedUserSource.content);
    }
  };
};

// Every writer of a note, Blueprint or block name passes through here: inputs that edit it,
// Blueprint names taken from a file name or URL, and names the agent sets. Keeps a surrogate
// pair whole rather than splitting it.
const capDocumentName = (name: string) => {
  if (name.length <= MAX_DOCUMENT_NAME_LENGTH) return name;
  const lastCode = name.charCodeAt(MAX_DOCUMENT_NAME_LENGTH - 1);
  const splitsSurrogatePair = lastCode >= 0xd800 && lastCode <= 0xdbff;
  return name.slice(0, splitsSurrogatePair ? MAX_DOCUMENT_NAME_LENGTH - 1 : MAX_DOCUMENT_NAME_LENGTH);
};

const runBestEffort = async (label: string, run: () => Promise<unknown>): Promise<void> => {
  try {
    await run();
  } catch (error) {
    console.error(`[rxdb-middleware] ${label} failed:`, error);
  }
};

export const getPostSave = ({
  rxCollection,
  rxCollections,
}: {
  rxCollection: RxCollection;
  rxCollections: RxDatabaseCollections;
}): MiddlewareBuilderResponse => {
  if (rxCollection.name === 'blocks') {
    return postSaveBlock(rxCollections.notes);
  }

  return undefined;
};

const postSaveBlock = (notesCollection: RxCollection<Note>) => {
  return async (block: Block) => {
    await updateNotesReferencingBlock(notesCollection, block);
  };
};

export const getPostRemove = ({
  rxCollection,
  rxCollections,
}: {
  rxCollection: RxCollection;
  rxCollections: RxDatabaseCollections;
}): MiddlewareBuilderResponse => {
  // The users collection carries no doc-lifecycle side effects (template/instruction
  // references) for postRemove to clean up.
  if (rxCollection.name === 'users') {
    return undefined;
  }

  if (rxCollection.name === 'tags') {
    return getPostRemoveTag(rxCollections);
  }

  // Tag counts are not touched here: they are applied from the committed change stream
  // (tag-handler/tag-count-events.ts), which sees this delete as a DELETE event.
  return async (rxJSON: RxJSON) => {
    const cleanupPromises: Promise<any>[] = [];

    if (rxCollection.name === 'userSources') {
      // Best-effort GC (readers stop trusting raw ref-map keys) — detached so it doesn't block the delete interaction.
      void removeReferenceFromNotes(rxCollections.notes, rxJSON.id, 'userSources');
    }

    if (rxCollection.name === 'instructions') {
      // Best-effort GC (readers stop trusting raw ref-map keys) — detached so it doesn't block the delete interaction.
      void cleanupDeletedInstructionReferences(rxCollections.notes, rxCollections.blocks, rxJSON.id);
    }

    if (rxCollection.name === 'notes') {
      cleanupPromises.push(cleanupTemplateReferencesOnDelete(rxCollections.notes, rxJSON as Note));
    }

    return Promise.allSettled(cleanupPromises);
  };
};

const getPostRemoveTag = (rxCollections: RxDatabaseCollections) => {
  return (tag: Tag) => {
    return detachTagFromDocs(rxCollections, tag.id);
  };
};

const duplicateDocumentInstructions = async (instructionsCollection: RxCollection<Instruction>, document: Note | Block) => {
  const oldStatusMap = document.instructions ?? {};
  const instructionIds = Object.keys(oldStatusMap);

  // Recreate instructions and build oldId -> newId mapping
  const idMapping: Record<string, string> = {};

  if (instructionIds.length) {
    const srcMap = await instructionsCollection.findByIds(instructionIds).exec();

    for (const oldId of instructionIds) {
      const srcDoc = srcMap.get(oldId);
      const srcJSON = srcDoc?.toJSON() as Instruction;

      if (srcJSON) {
        const now = getCurrDatetime();

        const created = await instructionsCollection.upsert({
          ...srcJSON,
          id: crypto.randomUUID(),
          updatedAt: now,
          createdAt: now,
        });

        idMapping[oldId] = created.id;
      }
    }
  }

  // Remap enabled statuses to the new IDs.
  // If the source instruction doc wasn't found, don't emit an `undefined` key.
  // This avoids dangling/invisible instruction references on notes/blocks.
  return Object.fromEntries(
    Object.entries(oldStatusMap).flatMap(([oldId, enabled]): [string, boolean][] =>
      idMapping[oldId] ? [[idMapping[oldId], enabled]] : []
    )
  );
};
